import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';

const ESignature = ({ templateId = 1, onSuccess }) => {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signature, setSignature] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const canvasRef = useRef(null);
  let drawing = false;

  useEffect(() => {
    fetchPendingOrders();
  }, []);

  const fetchPendingOrders = async () => {
    try {
      const res = await api.get('/orders/pending-agreements.php');
      setPendingOrders(res.data.pending_orders || []);
      if (res.data.pending_orders && res.data.pending_orders.length > 0) {
        setSelectedOrder(res.data.pending_orders[0]);
        setSignerName('');
        setSignerEmail('');
      }
    } catch (err) {
      console.error('Failed to fetch pending orders', err);
    } finally {
      setLoading(false);
    }
  };

  // Drawing functions
  const startDrawing = (e) => {
    drawing = true;
    draw(e);
  };
  const stopDrawing = () => {
    drawing = false;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
  };
  const draw = (e) => {
    if (!drawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.beginPath();
    setSignature('');
  };
  const saveSignature = () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    setSignature(dataUrl);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!signature) {
      setError('Please draw your signature.');
      return;
    }
    if (!selectedOrder) {
      setError('Please select an order.');
      return;
    }
    if (!signerName.trim() || !signerEmail.trim()) {
      setError('Please enter your name and email.');
      return;
    }
    setSubmitting(true);
    setError('');

    // Placeholder – in production, compute this from the actual document presented to the signer
    const originalHash = 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855';

    try {
      const res = await api.post('/agreements/sign.php', {
        order_id: selectedOrder.id,
        template_id: templateId,
        agreement_type: 'retainer',
        signer_name: signerName,
        signer_email: signerEmail,
        signature_image: signature,
        original_document_hash: originalHash,
        session_id: document.cookie || 'client-session'
      });
      if (res.data.success) {
        if (onSuccess) onSuccess(res.data.agreement_id);
        alert('Order agreement signed successfully!');
        fetchPendingOrders(); // refresh list
        clearCanvas();
        setSignature('');
        setSignerName('');
        setSignerEmail('');
      } else {
        setError(res.data.error || 'Signing failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Loading pending orders...</div>;
  if (pendingOrders.length === 0) return <div className="empty-state">No pending agreements. All orders have signed agreements.</div>;

  return (
    <div className="esignature-container">
      <h2>Sign Order Agreement</h2>

      <div className="form-group">
        <label>Select Order</label>
        <select
          value={selectedOrder?.id || ''}
          onChange={(e) => {
            const order = pendingOrders.find(o => o.id === parseInt(e.target.value));
            setSelectedOrder(order);
          }}
          className="modern-select"
        >
          {pendingOrders.map(order => (
            <option key={order.id} value={order.id}>
              Order #{order.id} - ${order.total_amount} ({new Date(order.order_date).toLocaleDateString()})
            </option>
          ))}
        </select>
      </div>

      {/* PDF Preview with token for authentication */}
      <div className="pdf-preview">
        <label>Agreement to Sign</label>
        {selectedOrder && selectedOrder.template_path && (
          <iframe
            src={`/backend/api/agreements/view-pdf.php?path=${encodeURIComponent(selectedOrder.template_path)}&token=${localStorage.getItem('token')}`}
            title="Agreement Preview"
            className="pdf-iframe"
          />
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Your Full Name (Signer)</label>
          <input type="text" value={signerName} onChange={(e) => setSignerName(e.target.value)} required />
        </div>
        <div className="form-group">
          <label>Your Email</label>
          <input type="email" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)} required />
        </div>
        <div className="form-group">
          <label>Draw Signature Below</label>
          <canvas
            ref={canvasRef}
            width={400}
            height={150}
            style={{ border: '1px solid #ccc', background: '#fff', cursor: 'crosshair' }}
            onMouseDown={startDrawing}
            onMouseUp={stopDrawing}
            onMouseMove={draw}
            onMouseLeave={stopDrawing}
          />
          <div className="signature-buttons">
            <button type="button" onClick={clearCanvas}>Clear</button>
            <button type="button" onClick={saveSignature}>Save Signature</button>
          </div>
          {signature && <img src={signature} alt="Signature preview" style={{ maxWidth: 200, marginTop: 10 }} />}
        </div>
        {error && <div className="error-message">{error}</div>}
        <button type="submit" disabled={submitting}>
          {submitting ? 'Submitting...' : 'Sign Agreement'}
        </button>
      </form>
    </div>
  );
};

export default ESignature;